import * as React from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router';
import { bindActionCreators } from 'redux';
import * as _ from 'underscore';

import { Messages } from '../../../codegen/Localize';
import { angularProviderHOC } from '../../../common/react/AngularProviderHOC';
import { DatasourceDetailsDialogCtrl } from '../../../ts/controllers/DatasourceDetailsDialogCtrl';
import { DatasourcePublishResultService } from '../../../ts/controllers/DatasourcePublishResultCtrl';
import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { Filter } from '../../../ts/helpers/Filter';
import { Omit } from '../../../ts/helpers/TypescriptHelpers';
import { DatasourceDefinition } from '../../../ts/models/ContentDefinitions';
import { IDataSourceDetailInfo, IDataSourceInfo } from '../../../ts/models/IDataSourceInfo';
import { INamedItemInfo } from '../../../ts/models/resourceInfoParts';
import { DataSources } from '../../../ts/resources/Datasources';
import { Projects } from '../../../ts/resources/Projects';
import { IGetPreferredRefreshOptionResult } from '../../../ts/ServerApi/requests/GetPreferredRefreshOption';
import { IDatasourceActions } from '../../../ts/ServerApi/types/IDatasourceActions';
import { FavoriteItemType } from '../../../ts/ServerApi/types/IFavorite';
import { IOrderClause } from '../../../ts/ServerApi/types/IOrderClause';
import { RefreshModeTypes } from '../../../ts/ServerApi/types/IRemoteRefreshProperties';
import { BrowserTitleService } from '../../../ts/services/BrowserTitleService';
import { DataCatalogApiRequestsService } from '../../../ts/services/DataCatalogApiRequestsService';
import { ExtractActionStatusService } from '../../../ts/services/ExtractActionStatusService';
import { ModalService } from '../../../ts/services/ModalService';
import { ServerService } from '../../../ts/services/ServerService';
import { Telemetry } from '../../../ts/services/TelemetryService';
import { setOrderAction } from '../../actions/SetOrderAction';
import { contentCountsUpToDate } from '../../actions/spaces/ContentCountsActions';
import { enterSpace } from '../../actions/spaces/EnterSpaceAction';
import { addContentFavorite, removeContentFavorite } from '../../actions/spaces/FavoritesActions';
import { requestSpaceContents } from '../../actions/spaces/RequestSpaceContentsActions';
import { setPreviousUrl } from '../../actions/spaces/SetPreviousUrlReducerAction';
import { updateBreadcrumbs } from '../../actions/UpdateBreadcrumbsAction';
import { appStore } from '../../AppStore';
import { addContentTypeToProjectHierarchy, populateContentBreadcrumbs } from '../../content/breadcrumbs/BreadcrumbHelpers';
import { ServiceLocator } from '../../ServiceLocator';
import { DatasourceSubspaceName, SpaceInfo } from '../../state/SpaceInfo';
import { MapDispatchToProps } from '../../utils/ConnectUtils';
import { DetailsDialogInAngularPlaceHelper } from '../DetailsDialog/AngularContentDetailsDialog';
import { ConnectedQueryParamUpdateProvider, QueryParamUpdateProviderOwnProps } from '../RouterHelpers/QueryParamUpdateProvider';
import { RouterError } from '../RouterHelpers/RouterError';
import { ConnectedSpaceInfoProvider } from '../RouterHelpers/SpaceInfoProvider';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { ConnectedDatasourceSpace } from './ConnectedDatasourceSpace';
import { DatasourceSpaceOwnProps } from './DatasourceSpace';

export interface DatasourceSpaceProviderOwnProps extends ReactDirectiveProps {
  readonly isNLPServiceEnabled: boolean;
  readonly routeLocationSearch: string;
  readonly subspaceName: DatasourceSubspaceName;
  readonly datasourceId: string;
  readonly urls: SpacesSiteUrls;
  readonly displayPublishResult: boolean;
  readonly publishResultUrl: string;
  readonly isWebExtractionEnabled: boolean;
  readonly renderDatasourceSpace: (props: DatasourceSpaceOwnProps) => JSX.Element;
  readonly fetchedAllData: () => void;
}

export interface DatasourceSpaceProviderDispatchProps {
  readonly contentCountsUpToDate: () => void;
  readonly enterSpace: (spaceInfo: SpaceInfo) => void;
  readonly requestSpaceContents: () => void;
  readonly setOrderAction: (order: ReadonlyArray<IOrderClause>) => void;
  readonly addContentFavorite: (contentId: string, contentType: FavoriteItemType) => void;
  readonly removeContentFavorite: (contentId: string, contentIdForFavoriting: string, contentType: FavoriteItemType, contentName: string) => void;
  readonly setPreviousUrl: (previousUrl: string) => void;
}

export interface DatasourceSpaceProviderPropsFromAngular {
  readonly dataSources: DataSources;
  readonly telemetry: Telemetry;
  readonly sanitizer: ng.sanitize.ISanitizeService;
  readonly modalService: ModalService;
  readonly serverService: ServerService;
  readonly q: ng.IQService;
  readonly browserTitleService: BrowserTitleService;
  readonly projects: Projects;
  readonly dataCatalogApiRequestsService: DataCatalogApiRequestsService;
  readonly datasourcePublishResultService: DatasourcePublishResultService;
  readonly extractActionStatusService: ExtractActionStatusService;
}

export type DatasourceSpaceNonAngularProps = DatasourceSpaceProviderOwnProps & DatasourceSpaceProviderDispatchProps;

export type DatasourceSpaceProviderContainerProps = DatasourceSpaceProviderOwnProps &  DatasourceSpaceProviderDispatchProps & DatasourceSpaceProviderPropsFromAngular;

export interface DatasourceSpaceProviderContainerState {
  readonly datasourceActions: IDatasourceActions;
  readonly datasourceInfo: IDataSourceInfo;
  readonly datasourceDetailInfo: IDataSourceDetailInfo;
  readonly initialRender: boolean;
  readonly renderChildren: boolean;
  readonly hasError: boolean;
  readonly preferredRefreshOption: IGetPreferredRefreshOptionResult;
}

const InitialState: DatasourceSpaceProviderContainerState = {
  datasourceActions: undefined,
  datasourceInfo: undefined,
  datasourceDetailInfo: undefined,
  initialRender: true,
  renderChildren: false,
  hasError: false,
  preferredRefreshOption: undefined
};

export class DatasourceSpaceProviderContainer extends React.PureComponent<DatasourceSpaceProviderContainerProps, DatasourceSpaceProviderContainerState> {

  private projectHierarchy: INamedItemInfo[] = undefined;
  private detailsDialogHelper: DetailsDialogInAngularPlaceHelper;
  private unmounted: boolean = false;

  public constructor(props: DatasourceSpaceProviderContainerProps){
    super(props);
    this.state =  InitialState;
  }

  public render() {
    if (this.state.renderChildren) {
      const redirect: JSX.Element = this.getRedirects();
      if (!!redirect) {
        return redirect;
      }

      return this.props.renderDatasourceSpace({
        datasourceDetail: {
          usageInfo: this.state.datasourceInfo.usageInfo,
          usageInfoMaxValues: this.state.datasourceInfo.usageInfoMaxValues,
          ...this.state.datasourceDetailInfo
        },
        datasourceActions: this.state.datasourceActions,
        openDetail: this.openDetail,
        addToFavorites: this.favorite,
        removeFromFavorites: this.unfavorite,
        sanitizer: this.props.sanitizer,
        telemetry: this.props.telemetry,
        showNLPSubspaces: this.state.datasourceActions.showNlp && this.props.isNLPServiceEnabled,
        bridgeEnabled: this.props.serverService.isBridgeEnabled(),
        dataCatalogApiRequestsService: this.props.dataCatalogApiRequestsService,
        angularContext: this.props.angularContext
      });

    } else if (this.state.hasError) {
      return <RouterError errorType={'datasources'} urls={this.props.urls} />;
    } else {
      return null;
    }
  }

  public getRedirects(): JSX.Element {
    const currSubspace = this.props.subspaceName;
    if ((currSubspace === 'nlp' || currSubspace === 'askDataUsageAnalytics') && (!this.state.datasourceActions.showNlp || !this.props.isNLPServiceEnabled)) {
      return <Redirect to={this.props.urls.datasourceConnectionsPath(this.props.datasourceId)} />;
    }

    if (currSubspace === 'extractRefreshes' && this.state.datasourceDetailInfo.refreshMode === RefreshModeTypes.Remote) {
      return <Redirect to={this.props.urls.datasourceLocalExtractRefreshesPath(this.props.datasourceId)} />;
    }

    if (currSubspace === 'remoteRefreshSchedules' && this.state.datasourceDetailInfo.refreshMode !== RefreshModeTypes.Remote) {
      /// Don't assume the refresh mode property is defined. If it isn't, 'extractTasks' is the default state.
      return <Redirect to={this.props.urls.datasourceExtractRefreshesPath(this.props.datasourceId)} />;
    }

    return null;
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  public componentDidMount() {
    this.getData();
  }

  public componentDidUpdate(prevProps: DatasourceSpaceProviderContainerProps) {
    if (this.changedDatasource(prevProps)) {
      this.projectHierarchy = undefined;
      this.updateState(InitialState /* want the initial state for the new data source */);
      this.getData();
      return;
    }

    if (this.props.displayPublishResult && _.isUndefined(this.state.preferredRefreshOption)) {
      // displayPublishResult comes in after mounting often, so do a specific check for it here
      // and fetch the info it needs
      this.getPreferredRefreshOption();
    }

    // if we dont have all we need - do nothing
    if (!this.haveAllDataNeededToRender()) {
      return;
    }

    if (this.state.initialRender) {
      this.changeSubspace(false, undefined);

      this.detailsDialogHelper = new DetailsDialogInAngularPlaceHelper(
        this.getData,
        this.props.q,
        this.props.angularContext,
        this.props.modalService,
        this.props.telemetry
      );

      this.setState({initialRender: false, renderChildren: true}, () => {
        if (this.props.displayPublishResult) {
          this.props.datasourcePublishResultService.handlePublishedDatasource(
            this.state.datasourceDetailInfo,
            this.state.preferredRefreshOption,
            this.props.publishResultUrl);

          this.removePublishDialogUrlParams();
        }
      });

      this.props.browserTitleService.setTitleWithPlaceEntityName({ placeEntityName: this.state.datasourceDetailInfo.name });

    } else if (prevProps.subspaceName !== this.props.subspaceName) {
      this.changeSubspace(true, prevProps.subspaceName);
    }
  }

  private getData = () => {
    this.getDatasource();
    this.getDatasourceDetail();
    this.getActions();
  }

  private changedDatasource(prevProps: DatasourceSpaceProviderContainerProps) {
    return !_.isUndefined(prevProps.datasourceId)
      && !_.isUndefined(this.props.datasourceId)
      && prevProps.datasourceId !== this.props.datasourceId;
  }

  private haveAllDataNeededToRender() {
    return !_.isUndefined(this.state.datasourceInfo) &&
      !_.isUndefined(this.state.datasourceDetailInfo) &&
      !_.isUndefined(this.state.datasourceActions) &&
      (!this.props.displayPublishResult || !_.isUndefined(this.state.preferredRefreshOption));
  }

  private getDatasourceDetail = async () => {
    try {
      const datasourceDetail = await this.props.dataSources.getById(this.props.datasourceId);
      this.updateState({datasourceDetailInfo: datasourceDetail});

      if (this.props.subspaceName === 'askDataUsageAnalytics') {
        this.populateBreadcrumbsForAskDataUsageAnalytics();

      } else {
        await this.getProjectHierarchyAndUpdateBreadcrumbs();
      }

      this.props.fetchedAllData();
      if (this.props.isWebExtractionEnabled) {
        this.pollAndUpdateExtractPendingStatus();
      }
    } catch (e) {
      this.updateState({ hasError: true });
    }
  }

  private getDatasource = async () => {
    try {
      const datasource = await this.props.dataSources.all()
        .withFilters([Filter.fieldEq('id', this.props.datasourceId)])
        .getFirst();
      this.updateState({datasourceInfo: datasource});
    } catch (e) {
      this.updateState({hasError: true});
    }
  }

  private getActions = async () => {
    try {
      const actions = await this.props.dataSources.actions(this.props.datasourceId);
      this.updateState({datasourceActions: actions});
    } catch (e) {}
  }

  private pollAndUpdateExtractPendingStatus = async () => {
    try {
      this.props.extractActionStatusService.pollAndUpdateExtractActionStatus(this.state.datasourceInfo);
      // if polling failed will do nothing
    } catch (e) {}
  }

  private getProjectHierarchyAndUpdateBreadcrumbs = async () => {
    const projectHierarchy = await this.props.projects.getProjectHierarchy(this.state.datasourceDetailInfo.project.id);
    this.projectHierarchy = projectHierarchy;
    this.populateBreadcrumbsWithProjectHierarchy(projectHierarchy);
  }

  private populateBreadcrumbsWithProjectHierarchy = (projectHierarchy: INamedItemInfo[]) => {
    const projectHierarchyWithType = addContentTypeToProjectHierarchy(projectHierarchy);
    populateContentBreadcrumbs([...projectHierarchyWithType, this.state.datasourceDetailInfo], this.props.urls);
  }

  private populateBreadcrumbsForAskDataUsageAnalytics = () => {
    appStore.dispatch(updateBreadcrumbs([
      {
        text: this.state.datasourceDetailInfo.name,
        href: this.props.urls.datasourceUrl(this.state.datasourceDetailInfo.id),
      }, {
        text: Messages.datasourceAskDataMetrics(),
      }
    ]));
  }

  private updateState<K extends keyof DatasourceSpaceProviderContainerState>(newState: Pick<DatasourceSpaceProviderContainerState, K>): void {
    if (!this.unmounted) {
      this.setState(newState);
    }
  }

  private changeSubspace = (shouldNotFetchCounts: boolean, prevSubspace: DatasourceSubspaceName) => {
    let subspaceName = this.props.subspaceName;

    this.props.enterSpace(
      {
        item: this.state.datasourceDetailInfo,
        spaceType: DatasourceDefinition.contentType,
        subspaceName: subspaceName
      }
    );

    // If we just switched tabs we know content counts are up to date.
    // They will get set to false by the above EnterSpaceAction.
    if(shouldNotFetchCounts) {
      this.props.contentCountsUpToDate();
    }

    if (subspaceName === 'askDataUsageAnalytics') {
      this.populateBreadcrumbsForAskDataUsageAnalytics();

    } else if (prevSubspace === 'askDataUsageAnalytics') {
      if (_.isUndefined(this.projectHierarchy)) {
        // if the previous subspace was askDataUsageAnalytics, then we need to reset the breadcrumbs and fetch project hierarchy.
        // Otherwise, the breadcrumbs are already correct
        // don't need to await on this function because it does not affect requestSpaceContents
        this.getProjectHierarchyAndUpdateBreadcrumbs();
      } else {
        this.populateBreadcrumbsWithProjectHierarchy(this.projectHierarchy);
      }
    }

    this.props.requestSpaceContents();
  }

  private openDetail = () => {
    const { datasourceActions, datasourceDetailInfo, datasourceInfo } = this.state;
    this.detailsDialogHelper.openDialog(
      this.state.datasourceDetailInfo,
      {
        controller: DatasourceDetailsDialogCtrl,
        resolve: {
          datasourceActions: () => datasourceActions,
          datasourceInfo: () => datasourceInfo,
          datasourceDetailInfo: () => datasourceDetailInfo,
          detailsDialogHelper: () => this.detailsDialogHelper
        }
      }
    );
  }

  private favorite = () => {
    this.props.addContentFavorite(this.state.datasourceDetailInfo.idForFavoriting, 'datasource');
  }

  private unfavorite = () => {
    this.props.removeContentFavorite(this.state.datasourceDetailInfo.id, this.state.datasourceDetailInfo.idForFavoriting, 'datasource', this.state.datasourceDetailInfo.name);
  }

  private getPreferredRefreshOption = async () => {
    if (this.props.displayPublishResult) {
      try {
        const preferredRefreshOption: IGetPreferredRefreshOptionResult = await this.props.dataSources.getPreferredRefreshOption(this.props.datasourceId);
        this.updateState({ preferredRefreshOption: preferredRefreshOption });
      } catch (e) {}
    }
  }

    /**
   * Prevents the publish dialog from showing up multiple times because of state.hardReload
   * re-rendering this react component and the previousUrl being the same in the app state.
   */
  private removePublishDialogUrlParams = () => {
    const cleanDSUrls = window.location.href;
    this.props.setPreviousUrl(cleanDSUrls);
  }
}

export namespace DatasourceSpaceProviderAngularProvider {
  function getAngularServicesWithServiceLocator(): DatasourceSpaceProviderPropsFromAngular {
    const dataSources = ServiceLocator.get<DataSources>(DataSources.ServiceLocatorKey);
    const telemetry = ServiceLocator.get<Telemetry>('TelemetryService');
    const sanitizer = ServiceLocator.get<ng.sanitize.ISanitizeService>('$sanitize');
    const modalService = ServiceLocator.get<ModalService>('ModalService');
    const serverService = ServiceLocator.get<ServerService>('ServerService');
    const q = ServiceLocator.get<ng.IQService>('$q');
    const browserTitleService = ServiceLocator.get<BrowserTitleService>('BrowserTitleService');
    const projects = ServiceLocator.get<Projects>('Projects');
    const dataCatalogApiRequestsService = ServiceLocator.get<DataCatalogApiRequestsService>('DataCatalogApiRequestsService');
    const datasourcePublishResultService = ServiceLocator.get<DatasourcePublishResultService>('DatasourcePublishResultService');
    const extractActionStatusService = ServiceLocator.get<ExtractActionStatusService>('ExtractActionStatusService');

    return {
      dataSources,
      telemetry,
      sanitizer,
      modalService,
      serverService,
      q,
      browserTitleService,
      projects,
      dataCatalogApiRequestsService,
      datasourcePublishResultService,
      extractActionStatusService
    };
  }

  function selectPropsFromAngularServices(angularServices: DatasourceSpaceProviderPropsFromAngular): DatasourceSpaceProviderPropsFromAngular {
    return angularServices;
  }

  export const HOC = angularProviderHOC<DatasourceSpaceNonAngularProps, DatasourceSpaceProviderPropsFromAngular, DatasourceSpaceProviderPropsFromAngular>({
    getAngularServicesWithServiceLocator,
    selectPropsFromAngularServices,
  });
}

const DatasourceSpaceProviderAng: React.ComponentType<DatasourceSpaceNonAngularProps> = DatasourceSpaceProviderAngularProvider.HOC(DatasourceSpaceProviderContainer);

const mapDispatchToProps: MapDispatchToProps<DatasourceSpaceProviderDispatchProps> = (dispatch) => bindActionCreators({
  contentCountsUpToDate,
  requestSpaceContents,
  enterSpace,
  addContentFavorite,
  removeContentFavorite,
  setOrderAction,
  setPreviousUrl
}, dispatch);

export const ConnectedDatasourceSpaceProviderContainer: React.ComponentClass<DatasourceSpaceProviderOwnProps>
  = connect(undefined, mapDispatchToProps)(DatasourceSpaceProviderAng);

const renderDatasourceSpace = (props: DatasourceSpaceOwnProps): JSX.Element => <ConnectedDatasourceSpace {...props} />;

export interface LocationPathProp {
  readonly routeLocationPath: string;
}
export type DatasourceSpaceProviderProps = LocationPathProp & Omit<DatasourceSpaceProviderOwnProps, 'renderDatasourceSpace' | 'fetchedAllData'>;

export interface DatasourceSpaceProviderState {
  readonly fetchedAllData: boolean;
}

export class DatasourceSpaceProvider extends React.PureComponent<DatasourceSpaceProviderProps, DatasourceSpaceProviderState> {
  public constructor(props: DatasourceSpaceProviderProps) {
    super(props);
    this.state = {fetchedAllData: false};
  }

  public render() {
    const queryUpdaterProps: QueryParamUpdateProviderOwnProps = {
      routeLocationPath:this.props.routeLocationPath,
      routeLocationSearch: this.props.routeLocationSearch,
      shouldUpdateContentTypes: true,
      shouldUpdateFilters: false,
      shouldUpdateSort: true,
      shouldUpdateBreadcrumbs: this.state.fetchedAllData
    };

    const DatasourceSpaceInfo: SpaceInfo = {
      item: null,
      spaceType: 'datasource',
      subspaceName: this.props.subspaceName,
    };

    return (
      <ConnectedSpaceInfoProvider spaceInfo={DatasourceSpaceInfo}>
        <ConnectedQueryParamUpdateProvider {...queryUpdaterProps}>
          <ConnectedDatasourceSpaceProviderContainer {...{...this.props, renderDatasourceSpace, fetchedAllData: this.fetchedAllData}} />
        </ConnectedQueryParamUpdateProvider>
      </ConnectedSpaceInfoProvider>
    );
  }

  private fetchedAllData = () => {
    this.setState({fetchedAllData: true});
  }
}
